<template>
  <div class="row justify-content-center" @click="onImageClick">
    <div class="col-12 col-xl-10">

      <!-- ══════════ Landing: chapter list ══════════ -->
      <div v-if="!current" class="card mb-4">
        <div class="card-body">
          <h3 class="mb-1">Notendahandbók</h3>
          <p class="text-secondary mb-2">
            Hér finnur þú leiðbeiningar um helstu aðgerðir í Tólspor, skipt í kafla eftir viðfangsefni.
            Það sem starfsfólk gerir oftast í símanum (stimpla sig inn, taka frá og skila tólum) er
            sýnt með skjámyndum úr síma.
          </p>
          <p v-if="isAdmin" class="text-secondary mb-3">
            Þú ert með <strong>stjórnandaaðgang</strong> og sérð því líka leiðbeiningar fyrir
            stjórnendur — þær eru neðst í hverjum kafla, undir <em>Fyrir stjórnendur</em>, auk kafla sem
            eingöngu eiga við stjórnendur.
          </p>
          <p v-else class="text-secondary mb-3">
            Handbókin sýnir það sem á við þinn aðgang. Valmyndir geta verið aðeins mismunandi eftir
            stillingum fyrirtækisins.
          </p>
          <div class="row g-3">
            <div v-for="c in visibleChapters" :key="c.id" class="col-12 col-md-6">
              <a href="#" class="manual-chapter" @click.prevent="openChapter(c.id)">
                <i class="material-icons manual-chapter-icon">{{ c.icon }}</i>
                <span>
                  <span class="d-block fw-bold">
                    {{ chapterTitle(c) }}
                    <span v-if="c.adminOnly" class="badge bg-dark ms-1 align-middle">Stjórnendur</span>
                  </span>
                  <span class="d-block text-sm text-secondary">{{ isAdmin && c.adminDesc ? c.adminDesc : c.desc }}</span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════ Chapter view ══════════ -->
      <template v-else>
        <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
          <button class="btn btn-sm btn-outline-dark mb-0" @click="openChapter(null)">
            <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">arrow_back</i>Efnisyfirlit
          </button>
          <h4 class="mb-0 d-flex align-items-center gap-2">
            <i class="material-icons">{{ current.icon }}</i>{{ chapterTitle(current) }}
          </h4>
        </div>

        <!-- ─────────── Fyrstu skrefin ─────────── -->
        <template v-if="current.id === 'getting-started'">
          <div class="card mb-4">
            <div class="card-body">
              <h5>Innskráning</h5>
              <p>
                Opnaðu Tólspor í símanum eða tölvunni og skráðu þig inn með netfanginu þínu
                (<em>Username</em>) og lykilorði (<em>Password</em>). Ef þú hefur gleymt lykilorðinu
                ýtirðu á <strong>Forgot Password</strong> og færð tölvupóst með hlekk til að velja nýtt.
                Nýir notendur fá boð í tölvupósti frá umsjónarmanni kerfisins til að virkja aðganginn.
              </p>
              <p>
                Ábending: settu Tólspor á heimaskjá símans svo það opnist eins og app — sjá kaflann
                <a href="#" @click.prevent="openChapter('app')">{{ $t('manual.pwa.title') }}</a>.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-login.jpg" alt="Innskráning" /><div class="cap">Innskráning í síma</div></div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Að rata um kerfið</h5>
              <p>
                Í símanum opnarðu valmyndina með <strong>☰</strong> hnappnum efst til vinstri; í tölvu er
                hún alltaf sýnileg vinstra megin. Þar eru m.a. <strong>Tólin mín</strong>,
                <strong>Tól</strong>, <strong>Verkefni</strong>, <strong>Notendur</strong> (listi yfir
                samstarfsfólk með netföngum og símanúmerum), <strong>Prófíll</strong> og
                <strong>Tímaskráning</strong>. Hvað birtist fer eftir hlutverki þínu og stillingum
                fyrirtækisins.
              </p>
              <p>
                Prófílmyndin efst í hægra horninu opnar flýtivalmynd með <strong>Prófíll</strong>,
                <strong>Dökkt þema</strong>, <strong>Notendahandbók</strong> (þessi síða) og
                <strong>Skrá út</strong>.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-menu.jpg" alt="Valmyndin í síma" /><div class="cap">Valmyndin opnuð með ☰</div></div>
                <div class="manual-phone"><img src="/manual/m-profile-menu.jpg" alt="Valmynd undir prófílmynd" /><div class="cap">Valmynd undir prófílmyndinni</div></div>
              </div>
              <p v-if="isAdmin" class="mt-3 mb-0">
                <span class="badge bg-dark me-1">Stjórnendur</span>
                Stjórnendur sjá auk þess m.a. <strong>Bílar &amp; VV</strong>, <strong>GPS tæki</strong>,
                <strong>Viðskiptamenn</strong>, <strong>Yfirlit</strong> (skýrslur) og
                <strong>Stillingar</strong>. Þessir hlutar eru hannaðir fyrir tölvu og eru sýndir með
                skjámyndum úr tölvu í þessari handbók.
              </p>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Tilkynningar</h5>
              <p>
                Bjallan efst í hægra horninu sýnir tilkynningar — t.d. þegar tól hefur verið skráð á þig
                lengur en hámarkstími segir til um. Rauða talan sýnir ólesnar tilkynningar; smelltu á
                bjölluna til að lesa þær. Undir <strong>Stillingar</strong> geturðu stillt hvort þú færð tilkynningarnar
                líka í tölvupósti eða sem push-skilaboð í símann.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-notifications.jpg" alt="Tilkynningar" /><div class="cap">Tilkynningar opnaðar</div></div>
              </div>
              <p v-if="isAdmin" class="mt-3 mb-0">
                <span class="badge bg-dark me-1">Stjórnendur</span>
                Stjórnendur sem kveikja á <strong>Fá stjórnendatilkynningar</strong> undir Stillingar → Notendastillingar fá einnig
                tilkynningar um biluð tól, útleigu, skoðun og þjónustu bíla og lága rafhlöðu í GPS
                sendum. Þær birtast alltaf í bjöllunni, en stillingin ræður hvort þær berast líka í
                tölvupósti eða síma.
              </p>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Prófíllinn þinn</h5>
              <p class="mb-3">
                Undir <strong>Prófíll</strong> sérðu vinnustaðarskírteinið þitt. Með
                <strong>Breyta prófíl</strong> breytirðu nafni, símanúmeri, prófílmynd og lykilorði.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-profile.jpg" alt="Prófíll" /><div class="cap">Vinnustaðarskírteinið</div></div>
              </div>
            </div>
          </div>
        </template>

        <!-- ─────────── Tímaskráning ─────────── -->
        <template v-if="current.id === 'time-registration'">
          <div class="card mb-4">
            <div class="card-body">
              <h5>Stimpla inn</h5>
              <p>
                Opnaðu <strong>Tímaskráning</strong>. Á flipanum <strong>Stimpla</strong> ýtirðu á
                <strong>Stimpla inn</strong> þegar þú byrjar að vinna, velur verkefnið sem þú ert að vinna
                í og ýtir á <strong>Staðfesta</strong>. Þú getur leitað að verkefni eftir númeri eða nafni.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-clock-idle.jpg" alt="Stimpla inn" /><div class="cap">1. Ýttu á Stimpla inn</div></div>
                <div class="manual-phone"><img src="/manual/m-clock-in-project.jpg" alt="Veldu verkefni" /><div class="cap">2. Veldu verkefni og ýttu á Staðfesta</div></div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Skipta um verk og stimpla út</h5>
              <p>
                Meðan þú ert stimplaður inn sérðu verkefnið og hve lengi þú hefur unnið við það. Þegar þú
                færir þig yfir á annað verk ýtirðu á <strong>Skipta</strong> og velur nýja verkefnið —
                fyrri færslan lokast og ný byrjar, svo hvert verk fær réttar stundir. Í lok dags ýtirðu á
                <strong>Stimpla út</strong>.
              </p>
              <p>
                Undir klukkunni sérðu <strong>Mínir tímar á launatímabili</strong> — heildarstundir þínar
                á yfirstandandi launatímabili — og undir <strong>Í dag</strong> færslurnar sem þú hefur
                lokið í dag.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-clock-running.jpg" alt="Stimplaður inn" /><div class="cap">Stimplaður inn: Skipta eða Stimpla út</div></div>
                <div class="manual-phone"><img src="/manual/m-clock-switch.jpg" alt="Skipta um verk" /><div class="cap">Skipta: veldu nýja verkefnið</div></div>
              </div>
              <p class="text-secondary text-sm mt-3 mb-0">
                Gleymdirðu að stimpla þig út? Kerfið lokar færslunni sjálfkrafa um nóttina á hefðbundnum
                lokatíma fyrirtækisins og merkir hana <strong>Sjálfvirk útstimplun</strong>. Athugaðu
                færsluna daginn eftir og leiðréttu tímann ef þarf.
              </p>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Leiðrétta færslu</h5>
              <p>
                Gleymdirðu að skipta um verk eða valdirðu rangt verkefni? Ýttu á blýantinn við færslu
                undir <strong>Í dag</strong> (eða við <em>Stimplaðist inn</em> á færslunni sem er í gangi)
                til að breyta tímunum eða verkefninu og ýttu á <strong>Vista</strong>. Færslur sem liggja
                hver að annarri færast með breyttum tíma. Eldri daga leiðréttirðu undir
                <strong>Yfirlit</strong>.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-clock-edit.jpg" alt="Breyta færslu" /><div class="cap">Breyta færslu</div></div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Skrá fjarveru</h5>
              <p>
                Ýttu á <strong>Skrá fjarveru</strong> á stimpilskjánum. Veldu tegund (t.d. veikindi,
                barnaveikindi, orlof eða leyfi) og dagsetningu — eða tímabil frá–til fyrir lengri
                fjarvistir eins og orlof. Ef fjarvistin er hluti úr degi (t.d. læknistími) skráirðu
                fjölda klukkustunda. Þegar þú skráir tímabil er helgum, frídögum og dögum sem þegar
                hafa færslu sleppt sjálfkrafa.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-absence.jpg" alt="Skrá fjarveru" /><div class="cap">Skrá fjarveru</div></div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Yfirlit og skil á launatímabili</h5>
              <p>
                Undir <strong>Yfirlit</strong> sérðu færslurnar þínar eftir dögum með vinnustundum og
                yfirvinnu. Veldu tímabil: <strong>Launatímabil (nú)</strong>,
                <strong>Launatímabil (síðasta)</strong>, <strong>Vika</strong> eða eigin dagsetningar með
                <strong>Velja</strong>. Hér geturðu leiðrétt eldri færslur, bætt við gleymdum degi með
                <strong>Skrá vinnudag</strong> og skrifað athugasemd við færslu.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-overview.jpg" alt="Yfirlit í síma" /><div class="cap">Yfirlit í síma</div></div>
              </div>
              <p class="mt-3">
                Yfirlitið er þægilegra í tölvu þegar fara þarf yfir heilt launatímabil:
              </p>
              <img src="/manual/time-overview.jpg" alt="Yfirlit í tölvu" class="manual-img" />
              <h6 class="mt-4">Að skila launatímabili</h6>
              <p>
                Í lok hvers launatímabils staðfestirðu tímana þína með <strong>Skila tímabili</strong>.
                Áður en þú skilar skaltu athuga að hver vinnudagur hafi réttar færslur, að fjarvistir séu
                skráðar og að færslur merktar <em>Sjálfvirk útstimplun</em> séu leiðréttar. Eftir skil
                læsist tímabilið — ef eitthvað reynist rangt hefurðu samband við stjórnanda, sem getur
                leiðrétt færsluna eða opnað tímabilið aftur.
              </p>
              <table class="table table-sm manual-table mb-0">
                <tbody>
                  <tr><td><span class="badge bg-secondary">Opið</span></td><td>Tímabilið er breytanlegt. Skilaðu því þegar tímarnir eru fullskráðir.</td></tr>
                  <tr><td><span class="badge bg-warning text-dark">Skilað</span></td><td>Þú hefur staðfest tímana. Læst fyrir þig; bíður samþykkis stjórnanda.</td></tr>
                  <tr><td><span class="badge bg-success">Samþykkt</span></td><td>Stjórnandi hefur samþykkt tímana til launavinnslu.</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <template v-if="isAdmin">
            <h6 class="manual-admin-divider"><i class="material-icons">admin_panel_settings</i>Fyrir stjórnendur</h6>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Yfirferð og samþykki launatímabila</h5>
                <p>
                  Undir <strong>Yfirlit</strong> velur stjórnandi starfsmann í fellilistanum efst. Veldu
                  <strong>Launatímabil (síðasta)</strong> eftir mánaðamót: stöðustikan sýnir hvort
                  starfsmaðurinn hefur skilað. Farðu yfir sjálfvirkar útstimplanir, óvenjulega yfirvinnu og
                  daga sem vantar, og ýttu svo á <strong>Samþykkja</strong> — eða
                  <strong>Opna aftur</strong> ef starfsmaðurinn þarf að laga eitthvað sjálfur. Hægt er að
                  samþykkja þótt starfsmaður hafi ekki skilað (t.d. í leyfi).
                </p>
                <p>
                  Stjórnendur geta leiðrétt færslur líka eftir skil; hver breyting er skráð með
                  upphaflegum tíma og nafni þess sem breytti. Með <strong>Sækja Excel</strong> sækirðu
                  launavinnslubók með öllum færslum og samantekt eftir starfsmönnum.
                </p>
                <img src="/manual/time-admin-approve.jpg" alt="Samþykki launatímabils" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Verkefnasýn</h5>
                <p>
                  Hnappurinn <strong>Verkefnasýn</strong> efst í yfirlitinu sýnir allar stundir á valið
                  verkefni, eftir dögum og starfsmönnum. Þar sjást bæði raunverulegir tímar
                  (<em>Raunv.</em>) og leiðréttir tímar ef leiðréttingar eru virkar.
                  <strong>Búa til skýrslu</strong> býr til útprentanlega tímaskýrslu fyrir verkefnið — t.d.
                  til að senda verkkaupa — og <strong>Taka yfirvinnustundir með</strong> ræður hvort
                  yfirvinna fylgir.
                </p>
                <img src="/manual/time-admin-project.jpg" alt="Verkefnasýn" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Tímaskýrslur og vinnustundir á verk</h5>
                <p>
                  Undir <strong>Yfirlit → Gagnagreining</strong> er flipinn <strong>Tímaskýrslur</strong>
                  með heildarstundum og yfirvinnu eftir vikum og hve margir starfsmenn hafa skráð sig.
                </p>
                <img src="/manual/time-reports.jpg" alt="Tímaskýrslur" class="manual-img" />
                <p class="mt-3">
                  <strong>Vinnustundir á verk</strong> sýnir skráðar stundir á hvert verk, sundurliðað í
                  dagvinnu, eftirvinnu, yfirvinnu og stórhátíð. Þar má setja tímagjald á verk, sjá
                  órukkaðar stundir og merkja þær rukkaðar eftir reikningsgerð svo þær verði aldrei
                  rukkaðar tvisvar.
                </p>
                <img src="/manual/labor-report.jpg" alt="Vinnustundir á verk" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Vinnuskipulag og frídagar</h5>
                <p class="mb-3">
                  Undir <strong>Stillingar → Grunngögn</strong> eru flipar fyrir <strong>Vinnuskipulag</strong>
                  (áætlaðar stundir hvern vikudag fyrir hvern starfsmann; sjálfgefið 8 klst mán–fös) og
                  <strong>Frídagar</strong>. Íslenskir frídagar eru innbyggðir, svo þú bætir aðeins við
                  sérdögum fyrirtækisins. Skipulagið ræður hvað telst dagvinna og hvað yfirvinna.
                </p>
                <img src="/manual/masterdata-schedules.jpg" alt="Vinnuskipulag" class="manual-img" />
              </div>
            </div>
          </template>
        </template>

        <!-- ─────────── Tól ─────────── -->
        <template v-if="current.id === 'tools'">
          <div class="card mb-4">
            <div class="card-body">
              <h5>Tólin mín</h5>
              <p>
                Forsíðan <strong>Tólin mín</strong> sýnir tólin sem eru skráð á þig: á hvaða verkefni,
                hvenær þau voru tekin frá og hve margir dagar eru eftir af hámarkstímanum (∞ ef enginn
                hámarkstími er). Með græna hnappnum við tólið skilarðu því.
              </p>
              <p>
                Táknin efst eru flipar: <strong>Mín tól</strong>, <strong>Mín saga</strong> (öll tól sem
                þú hefur haft), <strong>Bílar skráðir á mig</strong> og <strong>Vinnuvélar skráðar á
                mig</strong>.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-mytools.jpg" alt="Tólin mín" /><div class="cap">Tólin mín</div></div>
                <div class="manual-phone"><img src="/manual/m-history.jpg" alt="Mín saga" /><div class="cap">Mín saga</div></div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Taka frá tól með QR kóða</h5>
              <ol>
                <li>Ýttu á bláa <strong>myndavélarhnappinn</strong> á <strong>Tólin mín</strong>.</li>
                <li>Veldu <strong>Taka frá</strong> og beindu myndavélinni að QR kóðanum á tólinu.</li>
                <li>Veldu verkefnið sem tólið fer á og ýttu á <strong>Staðfesta</strong>. Tólið birtist
                  strax undir <strong>Tólin mín</strong>.</li>
              </ol>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-scan.jpg" alt="Taka frá eða skila" /><div class="cap">1–2. Myndavélarhnappurinn → Taka frá</div></div>
                <div class="manual-phone"><img src="/manual/m-tool-reserve.jpg" alt="Veldu verkefni" /><div class="cap">3. Veldu verkefni</div></div>
              </div>
              <p class="text-secondary text-sm mt-3 mb-0">
                Þú getur líka skannað QR kóðann með venjulegu myndavélinni í símanum — þá opnast
                tólasíðan. Ef fyrirtækið leyfir það geturðu einnig tekið tól frá beint á tólasíðunni með
                <strong>Aðgerðir → Taka frá</strong>.
              </p>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Skila tóli</h5>
              <p>
                Ýttu á myndavélarhnappinn, veldu <strong>Skila</strong> og skannaðu QR kóðann á tólinu.
                Eftir stillingum fyrirtækisins geturðu líka skilað með græna hnappnum undir
                <strong>Tólin mín</strong> eða með <strong>Aðgerðir → Skila</strong> á tólasíðunni.
                Stundum ertu beðinn um að taka mynd af tólinu við skil.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-tool-return.jpg" alt="Skila á tólasíðu" /><div class="cap">Aðgerðir → Skila á tólasíðunni</div></div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Finna tól</h5>
              <p>
                Undir <strong>Tól</strong> er listi yfir öll tól fyrirtækisins. Leitaðu eftir nafni eða
                lýsingu og síaðu eftir <strong>stöðu</strong> og <strong>týpu</strong>. Staða tóls getur
                t.d. verið <em>Laust</em>, <em>Frátekið</em>, <em>Lánað</em>, <em>Leigt</em> eða
                <em>Bilað</em> — og þegar tól er frátekið sést hver er með það, á hvaða verkefni eða í
                hvaða bíl.
              </p>
              <p>
                Ýttu á tól til að opna tólasíðuna með mynd, stöðu, staðsetningu á lager og sögu tólsins.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-tools.jpg" alt="Tólalistinn" /><div class="cap">Tólalistinn</div></div>
                <div class="manual-phone"><img src="/manual/m-tool.jpg" alt="Tólasíðan" /><div class="cap">Tólasíðan</div></div>
              </div>
            </div>
          </div>

          <template v-if="isAdmin">
            <h6 class="manual-admin-divider"><i class="material-icons">admin_panel_settings</i>Fyrir stjórnendur</h6>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Tólalistinn</h5>
                <p>
                  Stjórnendur sjá við hvert tól hnappa til að breyta (appelsínugulur) og eyða (rauður).
                  Með <strong>+</strong> efst stofnarðu nýtt tól og <strong>Flytja út tæki</strong> sækir
                  listann í Excel. Ef fjölleiga er virk seturðu laus tól í körfuna (græni hnappurinn) og
                  lánar eða leigir þau öll í einu.
                </p>
                <img src="/manual/tools.jpg" alt="Tólalistinn fyrir stjórnendur" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Nýtt tól</h5>
                <p class="mb-3">
                  Skráðu nafn, lýsingu, staðsetningu og týpu (skylda), og eftir þörfum vörumerki, raðnúmer,
                  dagsverð, kaupverð, gang og hillu á lager og hámarks frátökudaga. Settu inn mynd svo
                  auðvelt sé að þekkja tólið. Ef GPS sendir fylgir tólinu skráirðu raðnúmer hans og
                  tegund. Prentaðu svo QR kóðann og límdu hann á tólið.
                </p>
                <img src="/manual/tool-new.jpg" alt="Búa til tól" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Tólasíðan fyrir stjórnendur</h5>
                <p>
                  Stjórnendur sjá efst tekjur tólsins og hve oft það hefur unnið fyrir sér, og til hægri
                  QR kóðann. Veldu límmiðastærð og smelltu á kóðann til að prenta hann.
                </p>
                <img src="/manual/tool-detail.jpg" alt="Tólasíðan" class="manual-img" />
                <p class="mt-3">
                  Undir <strong>Aðgerðir</strong> eru m.a. <em>Prenta QR kóða</em>, <em>Lána/Leigja</em>,
                  <em>Taka frá</em>, stöðubreytingar (<em>Í viðgerð</em>, <em>Ónýtt</em>, <em>Laust</em>),
                  <em>PDF skjöl</em> (t.d. leiðbeiningar og vottorð), <em>Skoða GPS</em>,
                  <em>Breyta</em>, <em>Afrita</em> og <em>Eyða</em>. Hvað birtist fer eftir stöðu tólsins.
                  Neðst á síðunni eru <strong>Saga</strong>, <strong>Lána-/Leigusaga</strong> og
                  <strong>Viðgerðarsaga</strong>.
                </p>
                <img src="/manual/tool-actions.jpg" alt="Aðgerðavalmynd tóls" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Lána og leigja til utanaðkomandi</h5>
                <p>
                  <strong>Aðgerðir → Lána/Leigja</strong> skráir tól á aðila utan fyrirtækisins
                  (verktaka, viðskiptavin). Skráðu nafn, netfang og símanúmer leigutaka, veldu hvort tólið
                  er lánað eða leigt og tengdu verkefni ef þess er krafist. Leigutaki fær staðfestingu í
                  tölvupósti.
                </p>
                <img src="/manual/tool-loan.jpg" alt="Lána/Leigja" class="manual-img" />
                <p class="mt-3">
                  Lánuð og leigð tól eru á flipanum <strong>Tól Lánuð/Leigð</strong> á
                  <strong>Tólin mín</strong>, og þar er þeim skilað þegar þau koma til baka.
                </p>
                <img src="/manual/loaned.jpg" alt="Tól lánuð/leigð" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Tólatýpur og staðsetningar</h5>
                <p class="mb-3">
                  Undir <strong>Stillingar → Grunngögn</strong> heldurðu utan um <strong>Tól Týpur</strong>
                  (flokka sem notaðir eru í síum og skýrslum) og <strong>Staðsetningar</strong> (lagera og
                  starfsstöðvar).
                </p>
                <img src="/manual/masterdata.jpg" alt="Tólatýpur" class="manual-img" />
              </div>
            </div>
          </template>
        </template>

        <!-- ─────────── Bílar og vinnuvélar ─────────── -->
        <template v-if="current.id === 'cars'">
          <div class="card mb-4">
            <div class="card-body">
              <h5>Bíllinn þinn</h5>
              <p>
                Bílar og vinnuvélar sem eru skráð á þig birtast á flipunum <strong>Bílar skráðir á
                mig</strong> og <strong>Vinnuvélar skráðar á mig</strong> á <strong>Tólin mín</strong>.
                Bíl tekurðu frá eins og tól: með myndavélarhnappnum → <strong>Taka frá</strong> og QR
                kóðanum í bílnum. Tól sem eru skráð í bílinn fylgja honum þar til þau eru tekin úr honum.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-mycars.jpg" alt="Bílar skráðir á mig" /><div class="cap">Bílar skráðir á mig</div></div>
              </div>
            </div>
          </div>

          <template v-if="isAdmin">
            <h6 class="manual-admin-divider"><i class="material-icons">admin_panel_settings</i>Fyrir stjórnendur</h6>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Bílar og vinnuvélar</h5>
                <p class="mb-3">
                  Undir <strong>Bílar &amp; VV → Bílar</strong> (og <strong>Vinnuvélar</strong>) er listi
                  yfir tækin, fjöldi tóla í hverju og á hvern það er skráð. Með <strong>+</strong> skráirðu
                  nýjan bíl eða vinnuvél.
                </p>
                <img src="/manual/cars.jpg" alt="Bílar" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Bílasíðan</h5>
                <p class="mb-3">
                  Á bílasíðunni sérðu á hvern bíllinn er skráður, tólin í honum og smurningarsögu. Undir
                  <strong>Aðgerðir</strong> prentarðu QR kóða bílsins, skráir smurningu eða þjónustu,
                  skráir bílinn á notanda eða afskráir hann og stjórnar GPS sendi. Með <strong>+</strong>
                  við <strong>Tól í bíl</strong> bætirðu tólum í bílinn. Kerfið sendir tilkynningu þegar
                  skoðun eða þjónusta nálgast.
                </p>
                <img src="/manual/car-detail.jpg" alt="Bílasíðan" class="manual-img" />
              </div>
            </div>

            <div v-if="flags.enableGps" class="card mb-4">
              <div class="card-body">
                <h5>GPS tæki</h5>
                <p>
                  <strong>GPS tæki</strong> sýnir öll tól og bíla með GPS sendi á korti, með stöðu
                  rafhlöðu og hvenær sendirinn hafði síðast samband. Stjórnendur fá tilkynningu þegar
                  rafhlaða fer undir 10%.
                </p>
                <img src="/manual/gps-devices.jpg" alt="GPS tæki" class="manual-img" />
                <p class="mt-3">
                  <strong>Skoða GPS</strong> á tóla- eða bílasíðu sýnir ferilinn yfir valið tímabil —
                  bæði GPS hnit og staðsetningar frá QR skönnunum.
                </p>
                <img src="/manual/car-geotraces.jpg" alt="GPS ferill bíls" class="manual-img" />
              </div>
            </div>
          </template>
        </template>

        <!-- ─────────── Verkefni og skipulag ─────────── -->
        <template v-if="current.id === 'projects'">
          <div class="card mb-4">
            <div class="card-body">
              <h5>Verkefni</h5>
              <p>
                Undir <strong>Verkefni</strong> er listi yfir virk verkefni fyrirtækisins. Frátektir tóla
                og tímaskráningar tengjast alltaf verkefni. Hjá mörgum fyrirtækjum koma verkefnin
                sjálfkrafa úr bókhaldskerfinu.
              </p>
              <p>
                Ýttu á verkefni til að sjá hvaða tól eru skráð á það og hver er með þau, ásamt myndum og
                skjölum verkefnisins.
              </p>
              <div class="manual-phones">
                <div class="manual-phone"><img src="/manual/m-projects.jpg" alt="Verkefni" /><div class="cap">Verkefnalistinn</div></div>
                <div class="manual-phone"><img src="/manual/m-project.jpg" alt="Verkefnasíða" /><div class="cap">Tól skráð á verkefni</div></div>
              </div>
            </div>
          </div>

          <div v-if="showPlanner" class="card mb-4">
            <div class="card-body">
              <h5>Skipulag</h5>
              <p>
                <strong>Skipulag</strong> sýnir hver á að vinna á hvaða verkefni, eftir degi, viku eða
                mánuði. Með <strong>Notendur</strong> / <strong>Tól</strong> efst velurðu hvort raðirnar
                eru starfsmenn eða tæki. Smelltu á úthlutun til að sjá nánari upplýsingar.
                <template v-if="isAdmin">
                  Stjórnendur bæta við úthlutunum með <strong>Bæta við úthlutun</strong> eða með því að
                  smella í reit, og geta vistað fastar rútínur sem eru settar á heilan dag í einu.
                </template>
              </p>
              <img src="/manual/planner.jpg" alt="Skipulag" class="manual-img" />
            </div>
          </div>

          <template v-if="isAdmin">
            <h6 class="manual-admin-divider"><i class="material-icons">admin_panel_settings</i>Fyrir stjórnendur</h6>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Verkefnalistinn</h5>
                <p class="mb-3">
                  Stjórnendur sjá öll verkefni og geta síað eftir stöðu (<em>Ekki hafið</em>,
                  <em>Í gangi</em>, <em>Í bið</em>, <em>Lokið</em>). Eftir stillingum stofnarðu verkefni
                  með <strong>+</strong> eða sækir þau úr bókhaldskerfinu, og <strong>Flytja út
                  verkefni</strong> sækir listann í Excel.
                </p>
                <img src="/manual/projects.jpg" alt="Verkefnalistinn" class="manual-img" />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h5>Verkefnasíðan</h5>
                <p class="mb-3">
                  Stjórnendur sjá auk tóla á verkefninu flipana <strong>Saga</strong>, <strong>Tól
                  Lánuð/Leigð</strong> og <strong>Lána-/Leigusaga</strong>, geta bætt við myndum og PDF
                  skjölum og flutt tólalistann út í Excel. Kostnað tóla á verkefnum sérðu undir
                  <strong>Yfirlit → Reikningar</strong>.
                </p>
                <img src="/manual/project-detail.jpg" alt="Verkefnasíðan" class="manual-img" />
              </div>
            </div>

            <div v-if="flags.enableEndCustomers" class="card mb-4">
              <div class="card-body">
                <h5>Viðskiptamenn</h5>
                <p class="mb-3">
                  Undir <strong>Viðskiptamenn</strong> eru viðskiptavinir fyrirtækisins (verkkaupar) með
                  heimilisfangi og kennitölu. Verkefni eru tengd viðskiptamanni, sem nýtist í
                  reikningsgerð og skýrslunni <em>Reikningar viðskiptavina</em>. Eftir stillingum eru
                  viðskiptamenn samstilltir við bókhaldskerfið.
                </p>
                <img src="/manual/endcustomers.jpg" alt="Viðskiptamenn" class="manual-img" />
              </div>
            </div>
          </template>
        </template>

        <!-- ─────────── Notendur (admin) ─────────── -->
        <template v-if="current.id === 'users'">
          <div class="card mb-4">
            <div class="card-body">
              <h5>Notendalistinn</h5>
              <p>
                Undir <strong>Notendur</strong> eru allir starfsmenn. Með <strong>+</strong> býðurðu nýjum
                notanda — hann fær tölvupóst með hlekk til að velja lykilorð. Notendur sem hafa ekki
                klárað skráningu eru merktir og hægt að <strong>senda boðið aftur</strong>. Með
                <strong>Virkir</strong> / <strong>Óvirkir</strong> síarðu listann og <strong>Flytja út
                notendur</strong> sækir hann í Excel.
              </p>
              <img src="/manual/users.jpg" alt="Notendur" class="manual-img" />
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Breyta notanda og hlutverk</h5>
              <p>
                Opnaðu notanda og ýttu á <strong>Breyta prófíl</strong> til að breyta nafni, starfsheiti og
                <strong>hlutverki</strong>. Óvirkur notandi getur ekki skráð sig inn en saga hans helst.
                Stjórnandi getur ekki lækkað sitt eigið hlutverk.
              </p>
              <table class="table table-sm manual-table">
                <tbody>
                  <tr><td class="fw-bold">User</td><td>Starfsmaður: stimplar sig inn og út, tekur frá og skilar tólum, sér verkefni og skipulag.</td></tr>
                  <tr><td class="fw-bold">Admin</td><td>Stjórnandi: allt ofangreint og auk þess umsjón með tólum, bílum, verkefnum, notendum, samþykki tímaskráninga, skýrslum og stillingum.</td></tr>
                </tbody>
              </table>
              <img src="/manual/user-edit.jpg" alt="Breyta notanda" class="manual-img" />
            </div>
          </div>
        </template>

        <!-- ─────────── Skýrslur og stillingar (admin) ─────────── -->
        <template v-if="current.id === 'reports'">
          <div class="card mb-4">
            <div class="card-body">
              <h5>Gagnagreining</h5>
              <p>
                <strong>Yfirlit → Gagnagreining</strong> sýnir lykiltölur: fjölda notenda, verkefna, tóla
                og bíla, tól á verkefnum og notendum, kaupverð og stöðu tóla. Flipar efst opna nánari
                skýrslur, t.d. <em>Nýting tækja</em>, <em>Tekjur og útleiga</em>, <em>Vinnustundir á
                verk</em>, <em>Útrunnar frátektir</em>, <em>Fjarvistir og mæting</em> og
                <em>Reikningar viðskiptavina</em>. Hvaða skýrslur birtast fer eftir stillingum.
              </p>
              <img src="/manual/analytics.jpg" alt="Gagnagreining" class="manual-img" />
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Reikningar</h5>
              <p class="mb-3">
                <strong>Yfirlit → Reikningar</strong> reiknar kostnað tóla á hvert verkefni yfir valið
                tímabil, út frá dagsverði og frátektartíma. Ef tenging við bókhaldskerfi er virk má senda
                kostnaðinn beint þangað.
              </p>
              <img src="/manual/cost-report.jpg" alt="Reikningar" class="manual-img" />
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Kort</h5>
              <p class="mb-3">
                <strong>Yfirlit → Kort</strong> sýnir staðsetningar fyrirtækisins og tólin á þeim á korti
                af landinu.
              </p>
              <img src="/manual/map.jpg" alt="Kort" class="manual-img" />
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Admin stillingar</h5>
              <p class="mb-3">
                Undir <strong>Stillingar → Admin stillingar</strong> setur þú inn merki fyrirtækisins (birtist
                m.a. á QR límmiðum og vinnustaðarskírteinum), tengiliðaupplýsingar og almennar stillingar,
                t.d. blaðsíðustærð í töflum og hvort beðið sé um mynd við skil. Sumir eiginleikar eru
                virkjaðir af þjónustuaðila — hafðu samband ef þú vilt kveikja á nýjum möguleika.
              </p>
              <img src="/manual/customer-settings.jpg" alt="Admin stillingar" class="manual-img" />
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5>Grunngögn</h5>
              <p class="mb-0">
                <strong>Stillingar → Grunngögn</strong> heldur utan um flokka og uppflettigögn:
                verkefnatýpur, staðsetningar, tólatýpur, fyrirsettar verkefnatýpur og — ef tímaskráning
                er virk — frídaga og vinnuskipulag. Sjá einnig kaflana um tól og tímaskráningu.
              </p>
            </div>
          </div>
        </template>

        <!-- ─────────── Appið í símanum (translated) ─────────── -->
        <template v-if="current.id === 'app'">
          <div class="card mb-4">
            <div class="card-body">
              <p>{{ $t('manual.pwa.intro') }}</p>
              <ul class="mb-4">
                <li>{{ $t('manual.pwa.benefitIcon') }}</li>
                <li>{{ $t('manual.pwa.benefitFull') }}</li>
                <li>{{ $t('manual.pwa.benefitPush') }}</li>
              </ul>

              <h6 class="mt-4">{{ $t('manual.pwa.androidTitle') }}</h6>
              <ol>
                <li>{{ $t('manual.pwa.android1') }}</li>
                <li>{{ $t('manual.pwa.android2') }}</li>
                <li>{{ $t('manual.pwa.android3') }}</li>
                <li>{{ $t('manual.pwa.android4') }}</li>
              </ol>
              <div class="manual-phones">
                <div v-for="shot in androidShots" :key="shot.src" class="manual-phone">
                  <img :src="shot.src" :alt="shot.caption" />
                  <div class="cap">{{ shot.caption }}</div>
                </div>
              </div>
              <p class="text-secondary text-sm mt-3 mb-0">{{ $t('manual.pwa.androidNote') }}</p>

              <h6 class="mt-4">{{ $t('manual.pwa.iosTitle') }}</h6>
              <ol>
                <li>{{ $t('manual.pwa.ios1') }}</li>
                <li>{{ $t('manual.pwa.ios2') }}</li>
                <li>{{ $t('manual.pwa.ios3') }}</li>
                <li>{{ $t('manual.pwa.ios4') }}</li>
              </ol>
              <div class="manual-phones">
                <div v-for="shot in iosShots" :key="shot.src" class="manual-phone">
                  <img :src="shot.src" :alt="shot.caption" />
                  <div class="cap">{{ shot.caption }}</div>
                </div>
              </div>
              <p class="text-secondary text-sm mt-3 mb-0">{{ $t('manual.pwa.iosNote') }}</p>

              <h6 class="mt-4">{{ $t('manual.pwa.notifTitle') }}</h6>
              <p>{{ $t('manual.pwa.notifText') }}</p>

              <h6 class="mt-4">{{ $t('manual.pwa.tipTitle') }}</h6>
              <p class="mb-0">{{ $t('manual.pwa.tipText') }}</p>
            </div>
          </div>
        </template>

        <div v-if="nextChapter" class="d-flex justify-content-end mb-4">
          <button class="btn btn-sm btn-dark mb-0" @click="openChapter(nextChapter.id)">
            Næsti kafli: {{ chapterTitle(nextChapter) }}
            <i class="material-icons ms-1" style="font-size:1rem;vertical-align:middle">arrow_forward</i>
          </button>
        </div>
      </template>

      <!-- Image lightbox -->
      <ImageLightbox v-model:index="dialogImageIndex" :images="dialogImage ? [{ src: dialogImage }] : []" />

    </div>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import ImageLightbox from "../../components/ImageLightbox.vue";
import { getCustomerConfig } from "../Settings/customerConfigHelper";

const PWA_SHOT_LOCALES = ["en-US", "is-IS", "ar", "es-ES", "lt-LT", "pl-PL", "ro-RO", "sk-SK", "sl-SI"];

// requires: name of a flag in data.flags that must be true for the chapter to show
const CHAPTERS = [
  { id: "getting-started", icon: "flag", title: "Fyrstu skrefin", desc: "Innskráning, valmyndin, tilkynningar og prófíllinn þinn" },
  { id: "time-registration", icon: "schedule", title: "Tímaskráning", requires: "enableTimeRegistration",
    desc: "Stimpla inn og út, skipta um verk, fjarvistir og skil á launatímabili",
    adminDesc: "Stimplun og fjarvistir — og yfirferð, samþykki, skýrslur og vinnuskipulag" },
  { id: "tools", icon: "build", title: "Tól",
    desc: "Tólin mín, taka frá og skila með QR kóða, finna tól",
    adminDesc: "Taka frá og skila — og skráning tóla, QR kóðar, lán og leiga" },
  { id: "cars", icon: "directions_car", title: "Bílar og vinnuvélar", requires: "carsEnabled",
    desc: "Bílar og vinnuvélar sem eru skráð á þig",
    adminDesc: "Bílar, tól í bílum, smurning og GPS tæki" },
  { id: "projects", icon: "list_alt", title: "Verkefni og skipulag",
    desc: "Verkefnalistinn, tól á verkefnum og skipulag",
    adminDesc: "Verkefni, skipulag og viðskiptamenn" },
  { id: "users", icon: "groups", title: "Notendur", adminOnly: true, desc: "Bjóða notendum, hlutverk og óvirkir notendur" },
  { id: "reports", icon: "analytics", title: "Skýrslur og stillingar", adminOnly: true, desc: "Gagnagreining, reikningar, kort, stillingar og grunngögn" },
  { id: "app", icon: "install_mobile", titleKey: "manual.pwa.title", desc: "Settu Tólspor á heimaskjá símans" },
];

export default {
  name: "UserManual",
  components: {
    ImageLightbox,
  },
  data() {
    return {
      dialogImageIndex: null,
      dialogImage: "",
      isAdmin: false,
      flags: {
        enableTimeRegistration: false,
        carsEnabled: false,
        enableGps: false,
        enableEndCustomers: false,
        enablePlanner: false,
        enableFutureReservationProjectAssignments: false,
      },
    };
  },
  computed: {
    visibleChapters() {
      return CHAPTERS.filter(c => (!c.adminOnly || this.isAdmin) && (!c.requires || this.flags[c.requires]));
    },
    current() {
      return this.visibleChapters.find(c => c.id === this.$route.query.chapter) || null;
    },
    nextChapter() {
      const i = this.visibleChapters.indexOf(this.current);
      return i >= 0 ? this.visibleChapters[i + 1] || null : null;
    },
    // Same rule as the Skipulag link in SidenavList.vue
    showPlanner() {
      return this.flags.enableFutureReservationProjectAssignments || (this.flags.enablePlanner && this.isAdmin);
    },
    // The PWA screenshots carry browser-menu labels, so there is one set per locale.
    shotLocale() {
      const l = this.$i18n.locale;
      const s = (l && typeof l.value === "string") ? l.value : (typeof l === "string" ? l : "is-IS");
      return PWA_SHOT_LOCALES.includes(s) ? s : "en-US";
    },
    androidShots() {
      return [
        { src: `/manual/pwa-android-menu-${this.shotLocale}.jpg`, caption: this.$t("manual.pwa.capAndroidMenu") },
        { src: `/manual/pwa-android-install-${this.shotLocale}.jpg`, caption: this.$t("manual.pwa.capAndroidInstall") },
        { src: "/manual/pwa-home-screen.jpg", caption: this.$t("manual.pwa.capHome") },
      ];
    },
    iosShots() {
      return [
        { src: `/manual/pwa-ios-share-${this.shotLocale}.jpg`, caption: this.$t("manual.pwa.capIosShare") },
        { src: `/manual/pwa-ios-sheet-${this.shotLocale}.jpg`, caption: this.$t("manual.pwa.capIosSheet") },
        { src: `/manual/pwa-ios-add-${this.shotLocale}.jpg`, caption: this.$t("manual.pwa.capIosAdd") },
        { src: "/manual/pwa-home-screen.jpg", caption: this.$t("manual.pwa.capHome") },
      ];
    },
  },
  async created() {
    const token = Cookies.get("accessToken");
    if (token) {
      const role = jwtDecode(token).role;
      this.isAdmin = role === "Admin" || role === "SuperAdmin";
    }
    const [timeReg, disableCars, gps, endCustomers, planner, futureRes] = await Promise.all([
      getCustomerConfig("EnableTimeRegistration"),
      getCustomerConfig("DisableCars"),
      getCustomerConfig("EnableGPS"),
      getCustomerConfig("EnableEndCustomers"),
      getCustomerConfig("EnablePlanner"),
      getCustomerConfig("EnableFutureReservationProjectAssignments"),
    ]);
    this.flags = {
      enableTimeRegistration: timeReg === true,
      carsEnabled: disableCars !== true,
      enableGps: gps === true,
      enableEndCustomers: endCustomers === true,
      enablePlanner: planner === true,
      enableFutureReservationProjectAssignments: futureRes === true,
    };
  },
  methods: {
    chapterTitle(c) {
      return c.titleKey ? this.$t(c.titleKey) : c.title;
    },
    openChapter(id) {
      this.$router.push({ name: "User Manual", query: id ? { chapter: id } : {} });
      window.scrollTo({ top: 0 });
    },
    // Every screenshot zooms on click — one delegated handler instead of one per <img>
    onImageClick(e) {
      if (e.target.matches(".manual-img, .manual-phone img")) this.openImage(e.target.getAttribute("src"));
    },
    openImage(src) {
      this.dialogImage = src;
      this.dialogImageIndex = 0;
    },
  },
};
</script>

<style scoped>
.manual-img {
  width: 100%;
  max-width: 900px;
  display: block;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  cursor: zoom-in;
}

.manual-phones {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.75rem;
}

.manual-phone {
  width: 200px;
  max-width: 45%;
}

.manual-phone img {
  width: 100%;
  display: block;
  border-radius: 0.75rem;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  cursor: zoom-in;
}

.manual-phone .cap {
  font-size: 0.75rem;
  color: #67748e;
  margin-top: 0.4rem;
  line-height: 1.3;
}

.manual-chapter {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  height: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  color: inherit;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.manual-chapter:hover {
  border-color: #adb5bd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.manual-chapter-icon {
  font-size: 1.75rem;
  color: #344767;
}

.manual-admin-divider {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 2rem 0 1rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid #344767;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.manual-table td:first-child {
  white-space: nowrap;
  width: 1%;
}
</style>
